/**
 * My Permissions Page - Epic #17: User Permissions Viewer
 * Allows any authenticated user to view their own permissions
 * Carbon Design System compliant
 */

import React, { useState, useEffect } from 'react';
import {
  Content,
  Grid,
  Column,
  Heading,
  Stack,
  Accordion,
  AccordionItem,
  Tag,
  StructuredListWrapper,
  StructuredListHead,
  StructuredListRow,
  StructuredListCell,
  StructuredListBody,
  InlineNotification,
  Loading,
  Tile,
} from '@carbon/react';
import { UserRole, Security, Locked, Unlocked, Time } from '@carbon/icons-react';
import { useNavigate } from 'react-router-dom';
import styles from './ProfileSettingsPage.module.scss';

interface InstitutionalContext {
  institution_type: 'school' | 'district' | 'class' | 'platform';
  institution_id: string;
  institution_name: string;
  parent_institution_id?: string;
}

interface RoleInfo {
  role_id: string;
  role_name: string;
  hierarchy_level: number;
  institutional_context?: InstitutionalContext;
}

interface AccessScope {
  classes: Array<{ id: string; name: string }>;
  schools: Array<{ id: string; name: string }>;
  districts: Array<{ id: string; name: string }>;
  platform_access: boolean;
}

interface ExpiringRole {
  role_id: string;
  role_name: string;
  expiration_date: string;
}

interface UserPermissions {
  user_id: string;
  is_admin: boolean;
  roles: RoleInfo[];
  permissions: string[];
  institutional_contexts: InstitutionalContext[];
  access_scope: AccessScope;
  expiring_roles: ExpiringRole[];
  cache_info: {
    from_cache: boolean;
    computed_at: string;
    expires_at?: string;
    version?: number;
  };
}

const MyPermissionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);

  // Load permissions on mount
  useEffect(() => {
    loadPermissions();
  }, []);

  const loadPermissions = async () => {
    try {
      setLoading(true);
      setError(null);

      // Try multiple token keys for compatibility
      const token =
        localStorage.getItem('access_token') ||
        localStorage.getItem('sigma08_jwt_token') ||
        localStorage.getItem('auth_token') ||
        sessionStorage.getItem('access_token') ||
        sessionStorage.getItem('sigma08_jwt_token');

      if (!token) {
        navigate('/auth/login');
        return;
      }

      const response = await fetch('/api/v1/auth/my-permissions', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('auth_token');
          navigate('/auth/login');
          return;
        }
        throw new Error(`Failed to load permissions: ${response.statusText}`);
      }

      const data = await response.json();
      setPermissions(data);
    } catch (err) {
      console.error('Error loading permissions:', err);
      setError(err instanceof Error ? err.message : 'Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  const getPermissionColor = (
    permission: string
  ): 'blue' | 'cyan' | 'gray' | 'green' | 'magenta' | 'purple' | 'red' | 'teal' => {
    if (permission === '*' || permission.includes('admin')) return 'red';
    if (permission.includes(':manage') || permission.includes(':delete')) return 'magenta';
    if (permission.includes(':update') || permission.includes(':create')) return 'purple';
    if (permission.includes(':read') || permission.includes(':view')) return 'cyan';
    return 'gray';
  };

  const getRoleHierarchyColor = (
    level: number
  ): 'blue' | 'cyan' | 'gray' | 'green' | 'magenta' | 'purple' | 'red' | 'teal' => {
    if (level >= 10) return 'red'; // Platform admin
    if (level >= 7) return 'magenta'; // Principal/District admin
    if (level >= 5) return 'purple'; // Teacher
    if (level >= 3) return 'blue'; // Creator
    return 'gray'; // Student/Parent
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getDaysUntilExpiration = (expirationDate: string): number => {
    const now = new Date();
    const expiration = new Date(expirationDate);
    const diffTime = expiration.getTime() - now.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  if (loading) {
    return (
      <Content className={styles.content}>
        <Grid>
          <Column lg={16}>
            <Loading description="Loading permissions..." withOverlay={false} />
          </Column>
        </Grid>
      </Content>
    );
  }

  if (error) {
    return (
      <Content className={styles.content}>
        <Grid>
          <Column lg={16}>
            <InlineNotification kind="error" title="Error" subtitle={error} lowContrast />
          </Column>
        </Grid>
      </Content>
    );
  }

  if (!permissions) {
    return (
      <Content className={styles.content}>
        <Grid>
          <Column lg={16}>
            <InlineNotification
              kind="warning"
              title="No permissions found"
              subtitle="Unable to load your permission information."
              lowContrast
            />
          </Column>
        </Grid>
      </Content>
    );
  }

  return (
    <Content className={styles.content}>
      <Grid>
        <Column lg={16}>
          <Stack gap={6}>
            {/* Page Header */}
            <div className={styles.pageHeader}>
              <Heading className={styles.pageTitle}>
                <Security size={32} style={{ marginRight: '1rem' }} />
                My Permissions
              </Heading>
              <p className={styles.pageDescription}>
                View your current roles, permissions, and access scope in the system
              </p>
            </div>

            {/* Admin Badge */}
            {permissions.is_admin && (
              <InlineNotification
                kind="info"
                title="Administrator Access"
                subtitle="You have administrator privileges with access to all system features."
                lowContrast
              />
            )}

            {/* Expiring Roles Warning */}
            {permissions.expiring_roles && permissions.expiring_roles.length > 0 && (
              <InlineNotification
                kind="warning"
                title="Expiring Roles"
                subtitle={`You have ${permissions.expiring_roles.length} role(s) expiring soon.`}
                lowContrast
              >
                <Stack gap={3}>
                  {permissions.expiring_roles.map(role => {
                    const daysLeft = getDaysUntilExpiration(role.expiration_date);
                    return (
                      <div key={role.role_id}>
                        <strong>{role.role_name}</strong> expires in {daysLeft} day(s) on{' '}
                        {formatDate(role.expiration_date)}
                      </div>
                    );
                  })}
                </Stack>
              </InlineNotification>
            )}

            {/* Current Roles */}
            <Tile>
              <Heading style={{ marginBottom: '1rem' }}>
                <UserRole size={24} style={{ marginRight: '0.5rem' }} />
                Current Roles ({permissions.roles.length})
              </Heading>

              {permissions.roles.length === 0 ? (
                <p>No roles assigned</p>
              ) : (
                <Accordion>
                  {permissions.roles.map(role => (
                    <AccordionItem
                      key={role.role_id}
                      title={
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span>{role.role_name}</span>
                          <Tag type={getRoleHierarchyColor(role.hierarchy_level)} size="sm">
                            Level {role.hierarchy_level}
                          </Tag>
                        </div>
                      }
                    >
                      <Stack gap={4}>
                        <div>
                          <strong>Role ID:</strong> {role.role_id}
                        </div>

                        {role.institutional_context && (
                          <div>
                            <strong>Institutional Context:</strong>
                            <StructuredListWrapper>
                              <StructuredListBody>
                                <StructuredListRow>
                                  <StructuredListCell>Type</StructuredListCell>
                                  <StructuredListCell>{role.institutional_context.institution_type}</StructuredListCell>
                                </StructuredListRow>
                                <StructuredListRow>
                                  <StructuredListCell>Name</StructuredListCell>
                                  <StructuredListCell>{role.institutional_context.institution_name}</StructuredListCell>
                                </StructuredListRow>
                                <StructuredListRow>
                                  <StructuredListCell>ID</StructuredListCell>
                                  <StructuredListCell>{role.institutional_context.institution_id}</StructuredListCell>
                                </StructuredListRow>
                              </StructuredListBody>
                            </StructuredListWrapper>
                          </div>
                        )}
                      </Stack>
                    </AccordionItem>
                  ))}
                </Accordion>
              )}
            </Tile>

            {/* Effective Permissions */}
            <Tile>
              <Heading style={{ marginBottom: '1rem' }}>
                <Unlocked size={24} style={{ marginRight: '0.5rem' }} />
                Effective Permissions ({permissions.permissions.length})
              </Heading>

              {permissions.permissions.length === 0 ? (
                <p>No permissions assigned</p>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {permissions.permissions.map((permission, index) => (
                    <Tag key={index} type={getPermissionColor(permission)} size="md">
                      {permission}
                    </Tag>
                  ))}
                </div>
              )}
            </Tile>

            {/* Access Scope */}
            <Tile>
              <Heading style={{ marginBottom: '1rem' }}>
                <Locked size={24} style={{ marginRight: '0.5rem' }} />
                Access Scope
              </Heading>

              <Stack gap={4}>
                {permissions.access_scope.platform_access && (
                  <InlineNotification
                    kind="info"
                    title="Platform-wide Access"
                    subtitle="You have access across all institutions."
                    lowContrast
                    hideCloseButton
                  />
                )}

                {permissions.access_scope.districts.length > 0 && (
                  <div>
                    <strong>Districts ({permissions.access_scope.districts.length}):</strong>
                    <ul>
                      {permissions.access_scope.districts.map(district => (
                        <li key={district.id}>{district.name}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {permissions.access_scope.schools.length > 0 && (
                  <div>
                    <strong>Schools ({permissions.access_scope.schools.length}):</strong>
                    <ul>
                      {permissions.access_scope.schools.map(school => (
                        <li key={school.id}>{school.name}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {permissions.access_scope.classes.length > 0 && (
                  <div>
                    <strong>Classes ({permissions.access_scope.classes.length}):</strong>
                    <ul>
                      {permissions.access_scope.classes.map(cls => (
                        <li key={cls.id}>{cls.name}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {!permissions.access_scope.platform_access &&
                  permissions.access_scope.districts.length === 0 &&
                  permissions.access_scope.schools.length === 0 &&
                  permissions.access_scope.classes.length === 0 && <p>No institutional access defined</p>}
              </Stack>
            </Tile>

            {/* Cache Information */}
            <Tile>
              <Heading style={{ marginBottom: '1rem' }}>
                <Time size={24} style={{ marginRight: '0.5rem' }} />
                Cache Information
              </Heading>

              <StructuredListWrapper>
                <StructuredListBody>
                  <StructuredListRow>
                    <StructuredListCell>Source</StructuredListCell>
                    <StructuredListCell>
                      {permissions.cache_info.from_cache ? (
                        <Tag type="green" size="sm">
                          Cached
                        </Tag>
                      ) : (
                        <Tag type="gray" size="sm">
                          Fresh Computation
                        </Tag>
                      )}
                    </StructuredListCell>
                  </StructuredListRow>
                  <StructuredListRow>
                    <StructuredListCell>Computed At</StructuredListCell>
                    <StructuredListCell>{formatDate(permissions.cache_info.computed_at)}</StructuredListCell>
                  </StructuredListRow>
                  {permissions.cache_info.expires_at && (
                    <StructuredListRow>
                      <StructuredListCell>Expires At</StructuredListCell>
                      <StructuredListCell>{formatDate(permissions.cache_info.expires_at)}</StructuredListCell>
                    </StructuredListRow>
                  )}
                  {permissions.cache_info.version && (
                    <StructuredListRow>
                      <StructuredListCell>Cache Version</StructuredListCell>
                      <StructuredListCell>{permissions.cache_info.version}</StructuredListCell>
                    </StructuredListRow>
                  )}
                </StructuredListBody>
              </StructuredListWrapper>
            </Tile>
          </Stack>
        </Column>
      </Grid>
    </Content>
  );
};

export default MyPermissionsPage;
